import { useState, useEffect } from "react";
import { Service } from "@/types";
import { useServicesStore } from "@/lib/store";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Plus, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";

interface LabTestSelectorProps {
  value: string;
  onChange: (value: string) => void;
}

export function LabTestSelector({ value, onChange }: LabTestSelectorProps) {
  const { services, fetchServices } = useServicesStore();
  const [selectedTests, setSelectedTests] = useState<string[]>(value ? value.split(', ').filter(Boolean) : []);
  const [currentTestInput, setCurrentTestInput] = useState<string>("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  
  // Fetch services when component mounts
  useEffect(() => {
    fetchServices();
  }, []);
  
  // Filter services to only include laboratory tests (using category or other criteria)
  const labTests = services.filter(service => 
    service.category?.toLowerCase().includes('lab') || 
    service.category?.toLowerCase().includes('test')
  );

  // Filter lab tests based on input
  const filteredTests = currentTestInput.trim().length > 0
    ? labTests.filter(test => 
        test.name.toLowerCase().includes(currentTestInput.toLowerCase())
      )
    : labTests; // Show all tests when input is empty

  useEffect(() => {
    // Update the parent component value when selectedTests changes
    onChange(selectedTests.join(', '));
  }, [selectedTests, onChange]);

  useEffect(() => {
    const parsedFromValue = value
      ? value.split(',').map((test) => test.trim()).filter(Boolean)
      : [];

    // Keep internal state in sync when parent value changes (e.g. loading a draft)
    if (parsedFromValue.join('||') !== selectedTests.join('||')) {
      setSelectedTests(parsedFromValue);
    }
  }, [value, selectedTests]);

  const handleInputChange = (inputValue: string) => {
    setCurrentTestInput(inputValue);
    if (inputValue.trim().length > 0) {
      // Filter as user types
      const matches = labTests.filter(test => 
        test.name.toLowerCase().includes(inputValue.toLowerCase())
      );
      setShowDropdown(true);
      setHighlightedIndex(-1);
    } else {
      // Show all tests when input is cleared
      setShowDropdown(true);
      setHighlightedIndex(-1);
    }
  };

  const handleInputFocus = () => {
    // Show all tests when field is focused (even if empty)
    setShowDropdown(true);
    setHighlightedIndex(-1);
  };

  const handleInputBlur = () => {
    // Delay hiding dropdown to allow for click selection
    setTimeout(() => {
      setShowDropdown(false);
      setHighlightedIndex(-1);
    }, 200);
  };

  const handleTestSelect = (testName: string) => {
    if (!selectedTests.includes(testName)) {
      const updatedTests = [...selectedTests, testName];
      setSelectedTests(updatedTests);
      setCurrentTestInput("");
      setShowDropdown(false);
    }
  };

  const handleAddCustomTest = () => {
    const testName = currentTestInput.trim();
    if (testName && !selectedTests.includes(testName)) {
      const updatedTests = [...selectedTests, testName];
      setSelectedTests(updatedTests);
      setCurrentTestInput("");
      setShowDropdown(false);
    }
  };

  const handleRemoveTest = (test: string) => {
    const updatedTests = selectedTests.filter(t => t !== test);
    setSelectedTests(updatedTests);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showDropdown && filteredTests.length === 0) {
      if (e.key === 'Enter' && currentTestInput.trim()) {
        e.preventDefault();
        handleAddCustomTest();
      }
      return;
    }

    if (!showDropdown) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < filteredTests.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev > 0 ? prev - 1 : filteredTests.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < filteredTests.length) {
          handleTestSelect(filteredTests[highlightedIndex].name);
        } else if (currentTestInput.trim() && filteredTests.length === 0) {
          // If no matches, add as custom test
          handleAddCustomTest();
        }
        break;
      case 'Escape':
        setShowDropdown(false);
        setHighlightedIndex(-1);
        break;
    }
  };

  const hasMatches = filteredTests.length > 0;

  return (
    <div className="space-y-3">
      <div className="flex gap-3">
        <div className="flex-[2] relative">
          <Input
            value={currentTestInput}
            onChange={(e) => handleInputChange(e.target.value)}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            onKeyDown={handleKeyDown}
            placeholder="Type to search lab tests or enter custom test..."
            className="w-full"
          />
          
          {/* Auto-search dropdown */}
          {showDropdown && filteredTests.length > 0 && (
            <div 
              className="absolute z-50 w-full bottom-full mb-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto"
              role="listbox"
              aria-label="Lab test options"
            >
              {filteredTests.map((test, index) => (
                <div
                  key={test.id}
                  className={`px-3 py-2 cursor-pointer border-b border-gray-100 last:border-b-0 ${
                    index === highlightedIndex 
                      ? 'bg-blue-100 text-blue-900' 
                      : 'hover:bg-gray-100'
                  }`}
                  onMouseDown={(e) => {
                    e.preventDefault(); // Prevent input blur
                    handleTestSelect(test.name);
                  }}
                  role="option"
                  aria-selected={index === highlightedIndex}
                  tabIndex={-1}
                >
                  <span className="text-sm font-medium">{test.name}</span>
                  {test.category && (
                    <span className="text-xs text-gray-500 ml-2">({test.category})</span>
                  )}
                </div>
              ))}
            </div>
          )}
          
          {/* Custom test message when no matches */}
          {currentTestInput.trim().length > 0 && filteredTests.length === 0 && showDropdown && (
            <div className="absolute z-50 w-full bottom-full mb-1 bg-blue-50 border border-blue-200 rounded-md shadow-sm p-2 text-xs text-blue-700">
              No matching test found. Press Enter to add as custom test.
            </div>
          )}
        </div>
        <div className="flex-[1]">
          <Button 
            type="button" 
            onClick={() => {
              if (hasMatches && highlightedIndex >= 0 && highlightedIndex < filteredTests.length) {
                handleTestSelect(filteredTests[highlightedIndex].name);
              } else if (currentTestInput.trim() && filteredTests.length === 0) {
                handleAddCustomTest();
              } else if (hasMatches && filteredTests.length > 0) {
                // If there are matches but nothing highlighted, select first one
                handleTestSelect(filteredTests[0].name);
              }
            }} 
            disabled={!currentTestInput.trim()}
            className="w-full h-10"
          >
            <Plus className="h-4 w-4 mr-1" />
            {hasMatches ? "Add" : "Add Custom"}
          </Button>
        </div>
      </div>
      
      {selectedTests.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2">
          {selectedTests.map((test, index) => (
            <Badge key={index} variant="secondary" className="flex items-center gap-1">
              {test}
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-4 w-4 p-0 hover:bg-transparent" 
                onClick={() => handleRemoveTest(test)}
              >
                <X className="h-3 w-3" />
              </Button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}
